import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { applyClasses, applyProps } from '../shared/js';
import styles from './table.module.scss';

export const Table = ({ children, ...props }) => (
  <table
    id={props.id}
    // eslint-disable-next-line react/prop-types
    className={cn(styles.table, props.className, applyClasses(props, styles))}
    {...applyProps(props)}
  >
    {children}
  </table>
);

const THead = ({ children, ...props }) => (
  <thead
    id={props.id}
    // eslint-disable-next-line react/prop-types
    className={cn(styles.thead, props.className, applyClasses(props, styles))}
    {...applyProps(props)}
  >
    {children}
  </thead>
);

const TR = ({ children, ...props }) => (
  <tr
    id={props.id}
    // eslint-disable-next-line react/prop-types
    className={cn(styles.tr, props.className, applyClasses(props, styles))}
    {...applyProps(props)}
  >
    {children}
  </tr>
);

const TH = ({ colSpan, width, children, ...props }) => (
  <th
    colSpan={colSpan}
    id={props.id}
    // eslint-disable-next-line react/prop-types
    className={cn(styles.th, props.className, applyClasses(props, styles))}
    {...applyProps(props)}
    style={{ width: width }}
  >
    {children}
  </th>
);

const TD = ({ colSpan, rowSpan, children, ...props }) => (
  <td
    id={props.id}
    colSpan={colSpan}
    rowSpan={rowSpan}
    // eslint-disable-next-line react/prop-types
    className={cn(styles.td, props.className, applyClasses(props, styles))}
    {...applyProps(props)}
  >
    {children}
  </td>
);

const TBody = ({ children, ...props }) => (
  <tbody
    id={props.id}
    // eslint-disable-next-line react/prop-types
    className={cn(styles.tbody, props.className, applyClasses(props, styles))}
    {...applyProps(props)}
  >
    {children}
  </tbody>
);

Table.propTypes = {
  /** The ID of the table element. Useful to select the table during testing. */
  id: PropTypes.string,
  /** Applies some default styles to the table giving it a clean look */
  simple: PropTypes.bool,
  /** A class name string forwarded to the table element */
  className: PropTypes.string,
  /** A style object forwarded to the table element */
  style: PropTypes.object,
  /** The contents of the table */
  children: PropTypes.any,
};

Table.defaultProps = {
  className: undefined,
  style: undefined,
  children: undefined,
};

THead.propTypes = {
  /** The ID of the thead element. Useful to select the thead during testing. */
  id: PropTypes.string,
  /** A class name string forwarded to the thead element */
  className: PropTypes.string,
  /** A style object forwarded to the thead element */
  style: PropTypes.object,
  /** The contents of the thead */
  children: PropTypes.any,
};

THead.defaultProps = {
  className: undefined,
  style: undefined,
  children: undefined,
};

TH.propTypes = {
  /** The ID of the th element. Useful to select the th during testing. */
  id: PropTypes.string,
  /** The th colspan property */
  colSpan: PropTypes.number,
  /** Fix the width of the column */
  width: PropTypes.string,
  /** The contents of the th */
  children: PropTypes.any,
};

TH.defaultProps = {
  width: undefined,
  children: undefined,
};

TBody.propTypes = {
  /** The ID of the tbody element. Useful to select the tbody during testing. */
  id: PropTypes.string,
  /** The contents of the tbody */
  children: PropTypes.any,
};

TBody.defaultProps = {
  className: undefined,
  style: undefined,
  children: undefined,
};

TR.propTypes = {
  /** The ID of the tr element. Useful to select the tr during testing. */
  id: PropTypes.string,
  /** The contents of the tr */
  children: PropTypes.any,
};

TR.defaultProps = {
  children: undefined,
};

TD.propTypes = {
  /** The ID of the td element. Useful to select the td during testing. */
  id: PropTypes.string,
  /** The td colspan property */
  colSpan: PropTypes.number,
  /** The td rowspan property */
  rowSpan: PropTypes.number,
  /** The contents of the td */
  children: PropTypes.any,
};

TD.defaultProps = {
  children: undefined,
};

Table.THead = THead;
Table.TH = TH;
Table.TBody = TBody;
Table.TR = TR;
Table.TD = TD;
export default Table;
